import { cloneDeep } from 'lodash';

import { StructMapData } from './struct-map-types';

/**
 * Get all ids of nodes.
 *
 * @param data - StructMapData
 */
export function getAllNodesIds(data: StructMapData) {
  const ids: string[] = [data.id!];
  const subDivisions = data?.subDivisions || [];

  subDivisions.forEach((sd) => ids.push(...getAllNodesIds(sd)));
  return ids;
}

/**
 * Get all ids of leaves;
 * @param data - StructMapData
 */
export function getAllChildrenLeavesIds(data: StructMapData) {
  const ids: string[] = [];
  const subDivisions = data?.subDivisions || [];

  if (!!subDivisions.length) {
    subDivisions.forEach((sd) => ids.push(...getAllChildrenLeavesIds(sd)));
  }
  if (data.fileId) {
    ids.push(data.fileId!);
  }

  return ids;
}

/**
 * Removes nodes with id form nodeIds from hierarchy
 *
 * @param data - StructMap
 * @param nodeIds - ids of the node
 */
export function removeNodes(data: StructMapData, nodeIds: string[]) {
  const removeRoot = nodeIds.some((id) => id === data.id!);
  if (removeRoot) {
    return data;
  }
  const subDivisions = data.subDivisions || [];

  const found = subDivisions.find((sd) => nodeIds.some((id) => id === sd.id));

  if (found) {
    data.subDivisions = subDivisions.filter((sd) =>
      nodeIds.every((id) => id !== sd.id)
    );
  } else {
    data.subDivisions = subDivisions.map((sd) => removeNodes(sd, nodeIds));
  }

  return data;
}

/**
 * Adds new node as a child of target node inside the hierarchy
 *
 * @param data - StructMap
 * @param draggedNode - StructMap
 * @param targetId - id of target node
 */
export function addNode(
  data: StructMapData,
  draggedNode: StructMapData,
  targetId: string
) {
  const subDivisions = data?.subDivisions || [];

  if (data.id === targetId) {
    data.subDivisions = [...subDivisions, draggedNode];
  } else {
    data.subDivisions = subDivisions.map((sd) =>
      addNode(sd, draggedNode, targetId)
    );
  }
  return data;
}

/**
 * Move leaf/node into specified location inside of data hierarchy.
 *
 * @param data - StructMap
 * @param draggedNode - StructMap
 * @param targetId - id of target node
 */
export function moveNode(
  data: StructMapData,
  draggedNode: StructMapData,
  targetId: string
) {
  const draggedId = draggedNode.id!;
  const draggedChildren = getAllNodesIds(draggedNode);

  const targetInDraggedChildren = draggedChildren.find((ch) => ch === targetId);

  if (targetInDraggedChildren) {
    return data;
  }

  removeNodes(data, [draggedId, ...draggedChildren]);
  addNode(data, draggedNode, targetId);
}

/**
 * Edit label of specific node
 *
 * @param data
 * @param label
 * @param destinationId
 */
export function editLabel(
  data: StructMapData,
  label: string,
  destinationId: string
) {
  const subDivisions = data?.subDivisions || [];

  if (data.id === destinationId) {
    data.label = label;
  } else {
    subDivisions.forEach((child) => editLabel(child, label, destinationId));
  }

  return data;
}

/**
 * Get all files
 *
 * @param data - StructMapData
 */
export function getAllFiles(data: StructMapData) {
  const files: StructMapData[] = [];
  const subDivisions = data?.subDivisions || [];

  if (!!subDivisions.length) {
    subDivisions.forEach((sd) => files.push(...getAllFiles(sd)));
  }
  if (!!data?.aisId) {
    const copiedFile = cloneDeep(data);
    files.push(copiedFile);
  }

  return files;
}
